import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Utils from '../../../Utils';
import Badge from 'react-bootstrap/Badge';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Table from '../../../Components/Table/Table';

export default function T30v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t30Tests, setT30Tests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(true);

    const [selectedRows, setSelectedRows] = useState(t30Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getTest30TestsByUserId(currentUser._id);
            if (data) {
                setT30Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setTestsLoading(false);
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            meters50: 50,
            time30: '30:00,00',
            time50: '00:00,00',
            distance: 0,
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest30Test(currentUser._id, {
                time50Meters: time50,
                distance30Minutes: distance30,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });
            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });
            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = durationStr.match(/^([0-5]?\d):([0-5]\d),(\d{1,3})$/);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time50 = currentInput.time50 ? parseDuration(currentInput.time50) : 0;
    const distance30 = Number(currentInput.distance) || 0;

    const velocity50 = time50 ? 50 / time50 : 0;
    const velocity30 = currentInput.distance ? currentInput.distance / 1800 : 0;

    const calculateCSS = distance30Minutes => (distance30Minutes ? distance30Minutes / 1800 : 0);
    const calculateMSS = time50Meters => (time50Meters ? 50 / time50Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time50m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 50m (sec)</div>,
            accessor: 'time50m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time50Meters}</div>
        },
        {
            id: 'distance30m',
            Header: () => <div style={{ textAlign: 'center' }}>Distance 30 minutes (m)</div>,
            accessor: 'distance30m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.distance30Minutes}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div style={{ textAlign: 'center' }}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div style={{ textAlign: 'center' }}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div style={{ textAlign: 'center' }}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        time50Meters: time50,
        distance30Minutes: distance30,
        velocity50Meters: velocity50,
        velocity30Minutes: velocity30
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time50 > 0 && distance30 > 0 ? [currentTestData] : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        // Generate a unique color for each line (each test date)
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time50Meters, y: 50 },
                { x: 1800, y: row.distance30Minutes }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time50Meters > 0 && row.distance30Minutes > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity50Meters, y: 50 },
                    { x: row.velocity30Minutes, y: row.distance30Minutes }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPageWrap}>
            <div className={styles.topPageProgressBarWrap + ' island'}>
                <span>
                    <b>Test Difficulty:</b>
                </span>
                <div className={styles.progressBarWrap}>
                    <ProgressBar>
                        <ProgressBar animated striped variant='warning' now={50} />
                    </ProgressBar>
                </div>
            </div>
            <div className={styles.topPageWrap}>
                <div className={styles.currentTestAndSummary}>
                    <div className={styles.dataCollection + ' island'}>
                        <h2 className={styles.dataCollectionMainTitle}>Test Data</h2>
                        <h3 className={styles.dataCollectionSubTitle}>
                            Please enter your test data results.
                        </h3>
                        <form className={styles.dataCollectionForm}>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <label htmlFor='meters50'>Meters (m)</label>
                                    <input
                                        disabled={true}
                                        {...register('meters50', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='time50'>Duration (min)</label>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time50', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time50 && (
                                        <span className={styles.formInputError}>
                                            {errors.time50.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='velocity50'>Velocity (m/sec)</label>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.meters50,
                                            currentInput.time50
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <input
                                        type='number'
                                        {...register('distance', {
                                            required: 'Distance is required',
                                            valueAsNumber: true,
                                            validate: value =>
                                                value !== undefined && !isNaN(value) && value > 0
                                                    ? true
                                                    : 'Only positive numbers are allowed for distance'
                                        })}
                                        className='form-control'
                                    />
                                    {errors.distance && (
                                        <span className={styles.formInputError}>
                                            {errors.distance.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        placeholder='mm:ss,ms'
                                        type='text'
                                        {...register('time30', {
                                            required: true,
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.distance,
                                            currentInput.time30
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='poolSize'>
                                    <b>Pool Size (m)</b>[For reference only]
                                </label>
                                <select className='form-control' {...register('poolSize')}>
                                    <option value={''}>-</option>
                                    <option value={25}>25m</option>
                                    <option value={50}>50m</option>
                                </select>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='testDate'>
                                    <b>Test Date & Time</b> (Defaults to now)
                                </label>
                                <input
                                    type='datetime-local'
                                    {...register('testDate')}
                                    className='form-control'
                                />
                            </div>
                            <button
                                className={`${styles.dataCollectionFormButton} btn btn-primary`}
                                onClick={handleSubmit(onSubmit)}
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                    <div className={styles.dataSummary + ' island'}>
                        <h2 className={styles.summaryTitle}>Summary</h2>
                        <div className={styles.summaryGroup}>
                            <div className={styles.summaryItems}>
                                <div className={styles.badgeGroup}>
                                    <span className={styles.currentTestHeader}>
                                        Current Test Results
                                    </span>
                                    <span className={styles.currentTestHeader}>
                                        Latest CSS & MSS for{' '}
                                        <b>{currentUser ? currentUser.firstName : ''}</b>
                                    </span>
                                </div>
                                <Badge pill bg='primary' className={styles.customCSSBadge}>
                                    Critical Swim Speed (CSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateCSS(distance30).toFixed(3)}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentCSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                                <Badge pill bg='primary' className={styles.customMSSBadge}>
                                    Maximum Swim Speed (MSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateMSS(time50).toFixed(3) || 0}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentMSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.previousTestsWrap + ' island'}>
                    <h2 className={styles.previousTestsTitle}>Previous Tests</h2>
                    <Table
                        onRowClick={handleRowClick}
                        className={styles.previousTestsTable}
                        columns={columns}
                        data={t30Tests}
                        isLoading={testsLoading}
                        rowsPerPage={7}
                        updatePageSize={false}
                        pageSkipping={false}
                        selected={selectedRows}
                        showSelectionIndicator={true}
                        tableLayout='auto'
                    />
                </div>
                <div className={styles.dataInformation + ' island'}>
                    <h2 className={styles.informationTitle}>Information</h2>
                    <div className={styles.informationItems}>
                        <p>
                            This test is aimed for <b>advanced</b> or <b>intermediate</b> swimmers.
                            It takes <b>one</b> or <b>two</b> <b>days</b> to complete this test.
                            <br></br>
                            If you complete the test in <b>one</b> day, you must complete the{' '}
                            <b>50 m</b> test first and then, after at <b>least half an hour</b>, the
                            T30 test (30 minutes of swimming at the best possible pace).
                            <br></br>
                            If we complete the test in <b>two days</b>, we should complete one test
                            in one day and the second test in the next day.
                            <br></br>
                            The two trials will start in the pool without a start.
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.bottomPageWrap}>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        className={styles.chart}
                        title={'Distance - Time'}
                        titleY={'Distance (m)'}
                        titleX={'Time (sec)'}
                        datasets={datasetsDistanceTime}
                        xAxisType='linear'
                    />
                </div>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        title={'Distance - Velocity'}
                        titleY={'Distance (m)'}
                        titleX={'Velocity (m/sec)'}
                        datasets={datasetsDistanceVelocity}
                        xAxisType='linear'
                    />
                </div>
            </div>
            <div className={styles.footerPageWrap + ' island'}>
                <span className={styles.footerPageWrapNote}>
                    For your Training Zones Limits check your&nbsp;
                </span>
                <span className={styles.footerPageWrapNoteLink}>
                    <Link to='../../dashboard' style={{ color: 'inherit', textDecoration: 'none' }}>
                        Dashboard.
                    </Link>
                </span>
            </div>
        </div>
    );
}
